import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import UserLayout from '../../components/user/UserLayout';
import toast from 'react-hot-toast';
import { User, Lock, Save, Eye, EyeOff } from 'lucide-react';

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const [profileForm, setProfileForm] = useState({ name: user?.name || '', phone: user?.phone || '' });
  const [pwForm, setPwForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [showPw, setShowPw] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    try {
      await axios.put('/api/user/profile', profileForm);
      await refreshUser();
      toast.success('Profile updated successfully!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirmPassword) return toast.error('Passwords do not match');
    if (pwForm.newPassword.length < 6) return toast.error('Password must be at least 6 characters');
    setPwLoading(true);
    try {
      await axios.put('/api/user/change-password', { oldPassword: pwForm.oldPassword, newPassword: pwForm.newPassword });
      toast.success('Password changed successfully!');
      setPwForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Password change failed');
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <UserLayout>
      <div className="animate-fade-in max-w-2xl">
        <h1 className="text-2xl font-bold text-white mb-6">Profile Settings</h1>

        {/* Avatar & Info */}
        <div className="bg-dark-700 border border-white/5 rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-primary-500/20 rounded-2xl flex items-center justify-center text-3xl font-bold text-primary-400">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div>
              <p className="text-white font-semibold text-lg">{user?.name}</p>
              <p className="text-gray-400 text-sm">{user?.email}</p>
              <div className="flex items-center gap-3 mt-1">
                <span className={`text-xs px-2 py-0.5 rounded-full ${user?.isActive ? 'bg-primary-500/20 text-primary-300' : 'bg-red-500/20 text-red-300'}`}>
                  {user?.isActive ? 'Active' : 'Inactive'}
                </span>
                <span className="text-xs bg-dark-600 text-gray-300 px-2 py-0.5 rounded-full">Level {user?.level}</span>
                <span className="text-xs text-gray-500">Code: <span className="font-mono text-gray-300">{user?.referralCode}</span></span>
              </div>
            </div>
          </div>

          {/* Account Stats */}
          <div className="grid grid-cols-3 gap-3 pt-4 border-t border-white/5">
            <div className="text-center">
              <p className="text-white font-bold">${(user?.totalProfit || 0).toFixed(2)}</p>
              <p className="text-gray-500 text-xs">Total Profit</p>
            </div>
            <div className="text-center">
              <p className="text-white font-bold">{user?.directReferrals || 0}</p>
              <p className="text-gray-500 text-xs">Direct Referrals</p>
            </div>
            <div className="text-center">
              <p className="text-white font-bold">${(user?.totalCommissionEarned || 0).toFixed(2)}</p>
              <p className="text-gray-500 text-xs">Total Earned</p>
            </div>
          </div>
        </div>

        {/* Edit Profile */}
        <div className="bg-dark-700 border border-white/5 rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-5">
            <User className="w-5 h-5 text-primary-400" />
            <h3 className="text-white font-semibold">Personal Information</h3>
          </div>
          <form onSubmit={handleProfileSave} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Full Name</label>
              <input
                type="text"
                value={profileForm.name}
                onChange={e => setProfileForm({ ...profileForm, name: e.target.value })}
                className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Email Address</label>
              <input
                type="email"
                value={user?.email}
                disabled
                className="w-full bg-dark-600/50 border border-white/5 text-gray-500 rounded-xl px-4 py-3 cursor-not-allowed"
              />
              <p className="text-gray-600 text-xs mt-1">Email cannot be changed</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Phone Number</label>
              <input
                type="tel"
                value={profileForm.phone}
                onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })}
                className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500"
                placeholder="+91 XXXXX XXXXX"
              />
            </div>
            <button type="submit" disabled={profileLoading}
              className="w-full bg-primary-600 hover:bg-primary-500 disabled:bg-primary-800 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2">
              {profileLoading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                : <><Save className="w-4 h-4" /> Save Changes</>}
            </button>
          </form>
        </div>

        {/* Change Password */}
        <div className="bg-dark-700 border border-white/5 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-5">
            <Lock className="w-5 h-5 text-amber-400" />
            <h3 className="text-white font-semibold">Change Password</h3>
          </div>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Current Password</label>
              <div className="relative">
                <input
                  type={showPw ? 'text' : 'password'}
                  value={pwForm.oldPassword}
                  onChange={e => setPwForm({ ...pwForm, oldPassword: e.target.value })}
                  className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-3 pr-12 focus:outline-none focus:border-primary-500"
                  required
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">New Password</label>
              <input
                type="password"
                value={pwForm.newPassword}
                onChange={e => setPwForm({ ...pwForm, newPassword: e.target.value })}
                className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500"
                placeholder="Minimum 6 characters"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Confirm New Password</label>
              <input
                type="password"
                value={pwForm.confirmPassword}
                onChange={e => setPwForm({ ...pwForm, confirmPassword: e.target.value })}
                className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500"
                placeholder="Re-enter new password"
                required
              />
            </div>
            <button type="submit" disabled={pwLoading}
              className="w-full bg-amber-600 hover:bg-amber-500 disabled:bg-amber-900 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2">
              {pwLoading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                : <><Lock className="w-4 h-4" /> Update Password</>}
            </button>
          </form>
        </div>
      </div>
    </UserLayout>
  );
}
